import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { webColors, webBorderRadius } from '../../constants/webStyles';
import { useIsMobile } from '../../hooks/useIsMobile';

const WebProgressSteps = ({ steps, currentStep }) => {
  const isMobile = useIsMobile();
  if (isMobile) {
    const label = steps[currentStep - 1]?.label || '';
    return (
      <View style={[styles.container, styles.mobileContainer]}>
        <View style={styles.mobileTopRow}>
          <Text style={styles.mobileStepText}>Step {currentStep} of {steps.length}</Text>
          <Text style={styles.mobileStepLabel}>{label}</Text>
        </View>
        <View style={styles.mobileTrack}>
          <View style={[styles.mobileFill, { width: `${(currentStep / steps.length) * 100}%` }]} />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.stepsContainer}>
        {steps.map((step, index) => {
          const stepNumber = index + 1;
          const isActive = stepNumber === currentStep;
          const isCompleted = stepNumber < currentStep;

          return (
            <React.Fragment key={index}>
              <View style={styles.step}>
                <View style={[
                  styles.stepCircle,
                  isActive && styles.stepCircleActive,
                  isCompleted && styles.stepCircleCompleted,
                ]}>
                  {isCompleted ? (
                    <Text style={styles.checkmark}>✓</Text>
                  ) : (
                    <Text style={[
                      styles.stepNumber,
                      isActive && styles.stepNumberActive,
                    ]}>
                      {stepNumber}
                    </Text>
                  )}
                </View>
                <Text style={[
                  styles.stepLabel,
                  isActive && styles.stepLabelActive,
                  isCompleted && styles.stepLabelCompleted,
                ]}>
                  {step.label}
                </Text>
              </View>

              {index < steps.length - 1 && (
                <View style={[
                  styles.progressLine,
                  isCompleted && styles.progressLineActive,
                ]} />
              )}
            </React.Fragment>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: webColors.cardBackground,
    paddingVertical: 24,
    paddingHorizontal: 40,
    borderBottomWidth: 1,
    borderBottomColor: webColors.border,
  },
  stepsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 700,
    marginHorizontal: 'auto',
  },
  step: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  stepCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 2,
    borderColor: webColors.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepCircleActive: {
    backgroundColor: webColors.primary,
    borderColor: webColors.primary,
  },
  stepCircleCompleted: {
    backgroundColor: '#22c55e',
    borderColor: '#22c55e',
  },
  stepNumber: {
    fontSize: 14,
    fontWeight: '700',
    color: webColors.textMuted,
  },
  stepNumberActive: {
    color: '#ffffff',
  },
  checkmark: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '700',
  },
  stepLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textMuted,
  },
  stepLabelActive: {
    color: webColors.primary,
  },
  stepLabelCompleted: {
    color: '#22c55e',
  },
  progressLine: {
    width: 60,
    height: 3,
    backgroundColor: webColors.border,
    marginHorizontal: 8,
    borderRadius: 2,
  },
  progressLineActive: {
    backgroundColor: webColors.primary,
  },
  mobileContainer: {
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  mobileTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  mobileStepText: {
    fontSize: 13,
    fontWeight: '600',
    color: webColors.textMuted,
  },
  mobileStepLabel: {
    fontSize: 13,
    fontWeight: '700',
    color: webColors.primary,
  },
  mobileTrack: {
    height: 6,
    borderRadius: 3,
    backgroundColor: webColors.border,
    overflow: 'hidden',
  },
  mobileFill: {
    height: 6,
    borderRadius: 3,
    backgroundColor: webColors.primary,
  },
});

export default WebProgressSteps;
